import React, { PropsWithChildren, useMemo } from 'react';
import {
  DropType,
  IColSpanUpdater,
  IGridPosition,
  IRowDropPosition,
  IRowMinHeightUpdater,
} from './types';
import { GridHorizontalBorder } from './GridHorizontalBorder';
import styled from 'styled-components';

import { DND_GRID_GAP } from './constants';
import {
  DndGridRowColumnResizerContext,
  useCreateRowColumnResizerContext,
} from './useRowColumnResizer';
import { MaybeNil } from '../../utils/interfaces';

const Row = styled.div`
  display: flex;
  gap: ${DND_GRID_GAP}px;
  align-items: stretch;
`;

interface IProps extends PropsWithChildren<Pick<IGridPosition, 'rowIndex'>> {
  onUpdateColSpan: IColSpanUpdater;
  onUpdateRowMinHeight: IRowMinHeightUpdater;
  minHeight: MaybeNil<number>;
}

export const DndGridRow = ({
  children,
  rowIndex,
  minHeight,
  onUpdateColSpan,
  onUpdateRowMinHeight,
}: IProps) => {
  const rowColumnResizerContext = useCreateRowColumnResizerContext(
    minHeight,
    onUpdateColSpan,
    onUpdateRowMinHeight,
  );
  const { rowHeightInPixels, rowRefCallback } = rowColumnResizerContext;

  const topDropPosition = useMemo<IRowDropPosition>(
    () => ({ type: DropType.ROW, rowIndex }),
    [rowIndex],
  );

  const bottomDropPosition = useMemo<IRowDropPosition>(
    () => ({ type: DropType.ROW, rowIndex: rowIndex + 1 }),
    [rowIndex],
  );

  return (
    <DndGridRowColumnResizerContext.Provider value={rowColumnResizerContext}>
      {rowIndex === 0 ? (
        <GridHorizontalBorder position={topDropPosition} />
      ) : null}
      <Row
        ref={rowRefCallback}
        style={{
          minHeight: rowHeightInPixels ?? undefined,
        }}
      >
        {children}
      </Row>
      <GridHorizontalBorder position={bottomDropPosition} />
    </DndGridRowColumnResizerContext.Provider>
  );
};
